import React from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Form, Formik } from 'formik'
import { CheckCircleRounded } from '@material-ui/icons'

import { Typography, makeStyles, Box } from '@material-ui/core'

import { CLOSE, SECONDARY } from 'app/constants/button'
import { fullNameSchemaValidation } from 'commons/schemaValidation'

import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LinkToSignup from 'app/componentV4/Link/LinkToSignup'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import PanelsIllustrationMSG from 'app/componentV4/Panels/IllustrationMSG'

const useStyles = makeStyles({
  button: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
})

const CreateNewAccount = (props) => {
  const {
    email,
    code,
    onSubmit = () => {},
    isLoading = false,
    goTo = () => {},
    isPassword = false,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const initial = JSON.parse(localStorage.getItem('newAccountData'))

  return (
    <PanelsIllustrationMSG>
      <div className="flex justify-center items-center h-full">
        <div className="w-full h-full">
          {/* Header */}
          <Typography className="text-18 font-500 text-primary text-center">
            {'New user'}
          </Typography>

          <div className="flex items-center my-10">
            <div className="flex-1 border-t border-primary-350"></div>
            <Typography className="text-16 text-primary-300 text-center mx-5">
              {'Enter profile information'}
            </Typography>
            <div className="flex-1 border-t border-primary-350"></div>
          </div>

          <Formik
            initialValues={{
              code: code,
              email: initial?.email ?? email,
              firstName: initial?.firstName ?? '',
              lastName: initial?.lastName ?? '',
            }}
            validationSchema={Yup.object({
              ...fullNameSchemaValidation(formatMessage),
            })}
            onSubmit={async (value) => {
              await onSubmit(value)
              localStorage.removeItem('newAccountData')
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleBlur,
              handleChange,
            }) => (
              <>
                <Form className="space-y-10">
                  <LabelInput label={'Email Address'} required>
                    <SimpleInput
                      className={classes.input}
                      id="sign-up-input-email"
                      name="email"
                      placeholder={'Email Address'}
                      value={values.email}
                      size="medium"
                      disabled={true}
                      fullWidth
                      inputProps={{
                        'aria-label': 'Email sign up',
                      }}
                    />
                  </LabelInput>
                  <LabelInput
                    label={'First Name'}
                    required
                    className="w-full mb-0"
                  >
                    <SimpleInput
                      className={classes.input}
                      type={'text'}
                      id="sign-up-firstName-input"
                      name="firstName"
                      placeholder={'First Name'}
                      value={values.firstName}
                      size="medium"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      inputProps={{
                        'aria-label': 'FirstName sign up',
                        maxLength: 50,
                      }}
                      maxLength={50}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </LabelInput>

                  <LabelInput
                    label={'Last Name'}
                    required
                    className="w-full mb-0"
                  >
                    <SimpleInput
                      className={classes.input}
                      type={'text'}
                      id="sign-up-lastName-input"
                      name="lastName"
                      placeholder={'Last Name'}
                      value={values.lastName}
                      size="medium"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      inputProps={{
                        'aria-label': 'LastName sign up',
                        maxLength: 50,
                      }}
                      maxLength={50}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </LabelInput>

                  {/* Continue button */}
                  <div className="flex justify-center mt-10">
                    <ContainedButton
                      type="submit"
                      color={SECONDARY}
                      className="w-full h-48"
                      isLoading={isLoading || isSubmitting}
                      disabled={isLoading || isSubmitting}
                    >
                      {'Continue'}
                    </ContainedButton>
                  </div>
                </Form>
                <div className="flex items-center my-10">
                  <div className="flex-1 border-t border-primary-350"></div>
                  <Typography className="text-16 text-primary-300 text-center mx-5">
                    {'Optional setup'}
                  </Typography>
                  <div className="flex-1 border-t border-primary-350"></div>
                </div>

                {isPassword ? (
                  <Box className="rounded-8 py-5 px-10 bg-primary-200 flex items-center space-x-2">
                    <CheckCircleRounded
                      fontSize="small"
                      className="text-green-600"
                    />
                    <Typography className="text-16 text-primary">
                      {'Password set'}
                    </Typography>
                  </Box>
                ) : (
                  <OutlinedButtons
                    type="submit"
                    color={CLOSE}
                    className="w-full h-48"
                    onClick={() => {
                      localStorage.setItem(
                        'newAccountData',
                        JSON.stringify(values),
                      )
                      //keep before go to Setpassword
                      goTo()
                    }}
                  >
                    {'Setup Password'}
                  </OutlinedButtons>
                )}
                <LinkToSignup />
              </>
            )}
          </Formik>
        </div>
      </div>
    </PanelsIllustrationMSG>
  )
}

CreateNewAccount.propTypes = {
  goTo: PropTypes.func,
  code: PropTypes.string,
  email: PropTypes.string,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  isPassword: PropTypes.bool,
}

export default CreateNewAccount
